import { useState } from 'react';
import { Calendar, Eye, FileText, ShieldCheck } from 'lucide-react';
import PageHero from '../components/sections/PageHero';
import CtaBanner from '../components/sections/CtaBanner';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Badge from '../components/ui/Badge';
import { certifications, documentLibrary, regulations } from '../data/certifications';
import { site } from '../data/site';
import { formatDate } from '../lib/format';

export default function Compliance() {
  const [activeDoc, setActiveDoc] = useState(null);

  return (
    <>
      <PageHero
        eyebrow="Compliance & certifications"
        title="The Compliance Network Securing Your Documentation"
        description="We manage a strict chain of custody through a vetted network of CPCB-authorised facilities. Our platform ensures your EPR obligations are fully discharged and legally documented."
      >
        
      </PageHero>

      {/* Certifications */}
      <section className="section bg-white">
        <Container>
          <SectionHeading
            eyebrow="Certifications"
            title="Current, verifiable and available on request"
            description="Copies of every certificate below are shared during vendor onboarding and refreshed automatically on renewal."
          />

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {certifications.map((cert, index) => (
              <Reveal key={cert.id} delay={index * 0.06}>
                <div className="group flex h-full flex-col rounded-xl2 border border-ink-100 bg-surface p-7 transition-all duration-300 ease-smooth hover:-translate-y-1.5 hover:border-brand-200 hover:bg-white hover:shadow-lift">
                  <div className="flex items-start justify-between gap-4">
                    <span className="rounded-xl bg-brand-600 px-3 py-2 font-display text-sm font-bold text-white">
                      {cert.code}
                    </span>
                    <Badge className="border-brand-200 bg-brand-50 text-brand-700">
                      <Calendar className="h-3 w-3" />
                      {cert.validTill}
                    </Badge>
                  </div>

                  <h3 className="mt-6 font-display text-base font-semibold text-ink-900">
                    {cert.title}
                  </h3>
                  <p className="mt-1 text-xs font-medium text-ink-400">{cert.issuer}</p>
                  <p className="mt-4 text-sm leading-relaxed text-ink-500">{cert.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Regulatory framework */}
      <section className="section bg-surface">
        <Container>
          <SectionHeading
            eyebrow="Regulatory framework"
            title="The rules your disposal is measured against"
            description="A plain-language summary of what applies to bulk consumers and producers, and where our authorisation fits into each."
          />

          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {regulations.map((regulation, index) => (
              <Reveal key={regulation.id} delay={index * 0.08}>
                <article className="h-full rounded-xl2 border border-ink-100 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-lift">
                  <h3 className="font-display text-lg font-semibold text-ink-900">
                    {regulation.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-500">{regulation.summary}</p>
                  <ul className="mt-5 space-y-2.5 border-t border-ink-100 pt-5">
                    {regulation.points.map((point) => (
                      <li key={point} className="flex gap-2.5 text-sm text-ink-600">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-brand-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Document library */}
      <section className="section bg-white">
        <Container>
          <SectionHeading
            eyebrow="Document library"
            title="Specimens of what we issue"
            description="Preview the format of each document before you engage us, so your compliance team knows exactly what will land in their file."
          />

          <Reveal className="mt-14 overflow-hidden rounded-xl2 border border-ink-100 shadow-soft">
            <ul className="divide-y divide-ink-100">
              {documentLibrary.map((doc) => (
                <li
                  key={doc.id}
                  className="flex flex-col gap-4 bg-white p-5 transition-colors hover:bg-surface sm:flex-row sm:items-center sm:gap-6 sm:px-6"
                >
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-brand-50 text-brand-700">
                    <FileText className="h-5 w-5" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="font-display text-sm font-semibold text-ink-900">{doc.name}</p>
                    <p className="mt-1 text-xs text-ink-500">
                      {doc.type} · Updated {formatDate(doc.updated)}
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveDoc(doc)}
                    className="flex-none self-start sm:self-auto"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                </li>
              ))}
            </ul>
          </Reveal>

          <p className="mt-5 text-center text-xs text-ink-400">
            Specimens are illustrative. Live documents are issued against your consignment reference.
          </p>
        </Container>
      </section>

      <Modal
        open={Boolean(activeDoc)}
        onClose={() => setActiveDoc(null)}
        title={activeDoc?.name}
        description={activeDoc?.type}
        footer={
          <Button variant="outline" onClick={() => setActiveDoc(null)}>
            Close
          </Button>
        }
      >
        {activeDoc && (
          <div className="space-y-5">
            <p className="text-sm leading-relaxed text-ink-600">{activeDoc.description}</p>

            {/* Stylised document preview */}
            <div className="rounded-xl2 border border-ink-200 bg-surface p-6">
              <div className="flex items-center justify-between border-b border-ink-200 pb-4">
                <div>
                  <p className="font-display text-sm font-bold text-ink-900">{site.legalName}</p>
                </div>
                <ShieldCheck className="h-8 w-8 text-brand-600" strokeWidth={1.5} />
              </div>

              <p className="mt-5 font-display text-base font-semibold text-ink-900">
                {activeDoc.name.replace(' — Specimen', '')}
              </p>

              <div className="mt-4 space-y-2.5" aria-hidden="true">
                {[100, 92, 96, 74, 88, 60].map((width, index) => (
                  <div
                    key={index}
                    className="h-2.5 rounded-full bg-ink-200/70"
                    style={{ width: `${width}%` }}
                  />
                ))}
              </div>

              <div className="mt-6 flex items-end justify-between border-t border-ink-200 pt-4">
                <div className="space-y-2" aria-hidden="true">
                  <div className="h-2 w-24 rounded-full bg-ink-200/70" />
                  <div className="h-2 w-16 rounded-full bg-ink-200/70" />
                </div>
                <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-brand-700">
                  Specimen
                </span>
              </div>
            </div>

            <p className="text-xs text-ink-400">
              Last updated {formatDate(activeDoc.updated)}. Contact {site.email} for a certified copy.
            </p>
          </div>
        )}
      </Modal>

      <CtaBanner />
    </>
  );
}
